import { useMemo, useCallback } from 'react'

import { useRoute } from '@react-navigation/native'
import { Icons, Illustrations } from 'atoms'
import { AuthData, HIRING_SCREENS_ENUM } from 'integration/resources/auth'
import { FORMATION_TOKEN_KEY } from 'integration/resources/auth/requests'
import { MENU_FEATURES_ENUM, useGetHighlightedFeaturesQuery } from 'integration/resources/menu'
import { Box, ScrollView, Text, VStack } from 'native-base'
import { StyleSheet, Platform } from 'react-native'
import { useQueryClient } from 'react-query'
import { ZendeskAPI } from 'react-zendesk'
import screensNames from 'src/constants/screensNames'
import { useAccessLogs } from 'src/hooks/useAccessLogs'
import { useAuthAtomValue, useSetAuthAtom } from 'src/store/auth'
import { useSetTokenAtom } from 'src/store/token'
import { Storage } from 'src/utils'
import { getDeviceId } from 'src/utils/device'
import { LogEvent } from 'src/utils/logEvents'

import { ItemMenu } from './ItemMenu'
import Appconfig from '../../../../../app.config'

const allowedOrigins = ['https://hml.portalmaisturbo.com.br', 'https://portalmaisturbo.com.br']

const isOriginAllowed = (referrer: string) =>
  allowedOrigins.some((origin) => referrer.startsWith(origin))

const HIRING_BASE_PATH = '/contratacao'

const PAGE_MATCHERS = [
  { check: (path: string) => path === '/' || path.startsWith('/home'), name: 'Início' },
  { check: (path: string) => path.startsWith('/perfil'), name: 'Perfil' },
  { check: (path: string) => path.startsWith('/carteira'), name: 'Carteira' },
  { check: (path: string) => path.startsWith('/minha-organizacao'), name: 'Oportunidades' },
  { check: (path: string) => path.startsWith('/conteudos'), name: 'Conteúdos' },
  { check: (path: string) => path.startsWith('/paineis'), name: 'Painéis' },
  { check: (path: string) => path.startsWith('/pesquisas'), name: 'Pesquisas' },
  { check: (path: string) => path.startsWith('/contrata-ja'), name: 'Contrata Já' },
  { check: (path: string) => path.startsWith('/contratacao'), name: 'Contratação' },
  { check: (path: string) => path.startsWith('/hora-em-hora'), name: 'Hora em Hora' }
]

type HiringMenuConfig = {
  id: number
  title: string
  screen: HIRING_SCREENS_ENUM
  testID: string
  isVisible?: boolean
}

const buildHiringItems = (items: HiringMenuConfig[]) =>
  items
    .filter(({ isVisible }) => isVisible)
    .map(({ id, title, screen, testID }) => ({
      id,
      title,
      path: `${HIRING_BASE_PATH}/${screen}`,
      testID
    }))

export const handleSendSSOAuthDataFormation = async (authUser: AuthData | null) => {
  const originLocation = window?.location?.origin

  if (!isOriginAllowed(originLocation)) {
    // eslint-disable-next-line no-console
    console.error('Origin not allowed:', originLocation)

    return
  }

  const deviceId = await getDeviceId()

  const tokenFormation = await Storage.getItem(FORMATION_TOKEN_KEY) // this token is set in the pre-auth

  const targetOrigin = process.env.FORMACAO_PORTAL_URL || 'https://portal.formacaoturbo.com.br'

  const message = {
    type: 'SSO_AUTH_DATA_PORTAL',
    ssoInfo: authUser,
    deviceId
  }

  const newWindow = window.open(
    `${targetOrigin}/autenticacao-sso?token=${tokenFormation}`,
    '_blank'
  )

  LogEvent(`click_botao_formacao`, {
    std_code: authUser?.user?.std_code,
    user_name: authUser?.user?.name,
    user_email: authUser?.user?.email
  })

  setTimeout(() => {
    newWindow?.postMessage(message, targetOrigin)
  }, 1500)
}

export const handleSendSSOAuthDataAccelerate = async (authUser: AuthData | null) => {
  const originLocation = window?.location?.origin

  if (!isOriginAllowed(originLocation)) {
    // eslint-disable-next-line no-console
    console.error('Origin not allowed:', originLocation)

    return
  }

  const deviceId = await getDeviceId()

  const tokenSSO = await Storage.getItem(FORMATION_TOKEN_KEY) // this token is set in the pre-auth

  const targetOrigin = process.env.ACELERA_URL || 'https://painel.aceleraconsultor.com.br'

  const message = {
    type: 'SSO_AUTH_DATA_PORTAL',
    ssoInfo: authUser,
    deviceId
  }

  const newWindow = window.open(
    `${targetOrigin}/recrutador/autenticacao-sso?token=${tokenSSO}`,
    '_blank'
  )

  LogEvent(`click_botao_acelera`, {
    std_code: authUser?.user?.std_code,
    user_name: authUser?.user?.name,
    user_email: authUser?.user?.email
  })

  setTimeout(() => {
    newWindow?.postMessage(message, targetOrigin)
  }, 1500)
}

export const Sidebar = () => {
  const queryClient = useQueryClient()

  const route = useRoute()

  const auth = useAuthAtomValue()

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''

  const initialPageName = useMemo(() => {
    if (Platform.OS !== 'web') return ''

    const matched = PAGE_MATCHERS.find(({ check }) => check(currentPath))?.name

    if (matched) return matched

    const routeName = route?.name as keyof typeof screensNames | undefined

    if (routeName && screensNames[routeName]) {
      return screensNames[routeName]
    }

    return currentPath || 'Página desconhecida'
  }, [currentPath, route?.name])

  const { registerNavigation } = useAccessLogs({
    initialPageName,
    initialPagePath: currentPath
  })

  const trackExternalNavigation = useCallback(
    (pageName: string, pagePath: string, action: () => void) => {
      registerNavigation(pageName, pagePath, { trackNextPage: false })

      action()
    },
    [registerNavigation]
  )

  const { data } = useGetHighlightedFeaturesQuery()

  const featuredData = useMemo(() => data?.data?.data ?? [], [data?.data?.data])

  const dashboardsHasHighlight = featuredData.find(
    (item) => item.menu_key === MENU_FEATURES_ENUM.DASHBOARDS && !item.read
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const surveysHasHighlight = featuredData.find(
    (item) => item.menu_key === MENU_FEATURES_ENUM.SURVEYS && !item.read
  )

  const setAuthAtom = useSetAuthAtom()

  const setAuthToken = useSetTokenAtom()

  const handleLogout = () => {
    setAuthAtom(null)

    setAuthToken(null)

    queryClient.removeQueries()
  }

  // const isDev = process.env.Environment !== 'prd'

  const accessFlags = auth?.user

  const {
    is_visible_cockpit: seeCockpit,
    is_visible_accelerate: seeAccelerate,
    is_visible_wallet_management: seeWalletManagement,
    is_visible_goals: seeGoals,
    is_visible_delegation: seeDelegation,
    is_visible_migration: seeMigration,
    is_visible_wallet_management_sim: seeSIMWalletManagement,
    is_visible_goals_sim: seeSIMGoals,
    is_visible_delegation_sim: seeSIMDelegation,
    is_visible_migration_sim: seeSIMMigration,
    is_visible_hourly: seeHourly
  } = accessFlags ?? {}

  const isAdmin = auth?.user.is_admin

  const formationTarget = process.env.FORMACAO_PORTAL_URL || 'https://portal.formacaoturbo.com.br'

  const accelerateTarget = process.env.ACELERA_URL || 'https://painel.aceleraconsultor.com.br'

  const handleToggleZendesk = () => {
    ZendeskAPI('webWidget', 'open')

    ZendeskAPI('webWidget', 'setLocale', 'pt-BR')

    ZendeskAPI('webWidget', 'helpCenter:setSuggestions', { search: 'login' })
  }

  const hiringItems = useMemo(
    () =>
      buildHiringItems([
        {
          id: 1,
          title: 'Carteirização',
          screen: HIRING_SCREENS_ENUM.WALLETS_MANAGEMENT,
          testID: 'walletsManagement',
          isVisible: seeWalletManagement
        },
        {
          id: 2,
          title: 'Metas',
          screen: HIRING_SCREENS_ENUM.GOALS,
          testID: 'goals',
          isVisible: seeGoals
        },
        {
          id: 3,
          title: 'Delegação',
          screen: HIRING_SCREENS_ENUM.DELEGATION,
          testID: 'delegation',
          isVisible: seeDelegation
        },
        {
          id: 4,
          title: 'Migração',
          screen: HIRING_SCREENS_ENUM.MIGRATION,
          testID: 'migration',
          isVisible: seeMigration
        }
      ]),
    [seeWalletManagement, seeGoals, seeDelegation, seeMigration]
  )

  const hiringItemsSIM = useMemo(
    () =>
      isAdmin
        ? buildHiringItems([
            {
              id: 1,
              title: 'Carteirização',
              screen: HIRING_SCREENS_ENUM.WALLETS_MANAGEMENT,
              testID: 'walletsManagementSIM',
              isVisible: seeSIMWalletManagement
            },
            {
              id: 2,
              title: 'Metas',
              screen: HIRING_SCREENS_ENUM.GOALS,
              testID: 'goalsSIM',
              isVisible: seeSIMGoals
            },
            {
              id: 3,
              title: 'Delegação',
              screen: HIRING_SCREENS_ENUM.DELEGATION,
              testID: 'delegationSIM',
              isVisible: seeSIMDelegation
            },
            {
              id: 4,
              title: 'Migração',
              screen: HIRING_SCREENS_ENUM.MIGRATION,
              testID: 'migrationSIM',
              isVisible: seeSIMMigration
            }
          ])
        : [],
    [seeSIMWalletManagement, seeSIMGoals, seeSIMDelegation, seeSIMMigration, isAdmin]
  )

  const showHiringMenu = hiringItems.length > 0

  const showSIMHiringMenu = hiringItemsSIM.length > 0

  return (
    <VStack w="255px" position="relative" p={8} pr={0} pb={0} bg="background.base">
      <Box style={styles.background} />
      <VStack space={4}>
        <Icons.LogoTurbo size={130} />
        <Box opacity={1}>
          <Icons.PortalMaisTurbo />
        </Box>
      </VStack>
      <ScrollView showsVerticalScrollIndicator={false} position="relative" zIndex={3}>
        <Box height="83vh" justifyContent="space-between" mt={10} position="relative">
          <Box>
            <VStack space={4}>
              <ItemMenu testID="start" icon="FlameSantander" title="Início" path="/home" />
              <ItemMenu testID="profile" icon="User" title="Perfil" path="/perfil" />
              <ItemMenu
                testID="wallet"
                icon="Suitcase"
                title="Carteira"
                submenus={[
                  {
                    id: 1,
                    title: 'Contatos',
                    path: '/carteira/contatos',
                    testID: 'contacts'
                  },
                  {
                    id: 2,
                    title: 'TABs',
                    path: '/carteira/tabs',
                    testID: 'tabs'
                  }
                ]}
              />
              <ItemMenu
                isHighlighted={!!dashboardsHasHighlight}
                testID="opportunities"
                icon="Calendar"
                title="Oportunidades"
                path="/minha-organizacao/oportunidades/5"
              />
              {showHiringMenu && (
                <ItemMenu
                  testID="hiring"
                  icon="WalletsManagement"
                  title="Contratação"
                  submenus={hiringItems}
                />
              )}
              {showSIMHiringMenu && (
                <ItemMenu
                  testID="hiringSIM"
                  icon="WalletsManagement"
                  title="Contratação BENS"
                  submenus={hiringItemsSIM}
                />
              )}
              <ItemMenu testID="contents" icon="BookOpen" title="Conteúdos" path="/conteudos" />
              <ItemMenu
                isHighlighted={!!dashboardsHasHighlight}
                testID="dashboards"
                icon="Dashboards"
                title="Painéis"
                path="/paineis"
              />
              <ItemMenu
                // isHighlighted={!!surveysHasHighlight}
                testID="survey"
                icon="Survey"
                title="Pesquisas"
                path="/pesquisas"
              />
              <ItemMenu testID="hire-more" icon="Plus" title="Contrata Já" path="/contrata-ja" />
              {seeCockpit && (
                <ItemMenu
                  testID="cockpit"
                  icon="Cockpit"
                  title="ChamaAI"
                  path=""
                  onPress={() => {
                    trackExternalNavigation('ChamaAI', 'https://cockpitai.com.br/', () => {
                      LogEvent(`menu_link_cockpit`, {
                        item_id: ''
                      })

                      window.open('https://cockpitai.com.br/', '_blank', 'noopener')
                    })
                  }}
                />
              )}
              <ItemMenu
                testID="education"
                icon="Formation"
                title="Formação"
                path=""
                onPress={() =>
                  trackExternalNavigation('Formação', formationTarget, () =>
                    handleSendSSOAuthDataFormation(auth ?? null)
                  )
                }
              />
              {seeAccelerate && (
                <ItemMenu
                  testID="accelerate"
                  icon="Cockpit"
                  title="Acelera"
                  path=""
                  onPress={() =>
                    trackExternalNavigation('Acelera', accelerateTarget, () =>
                      handleSendSSOAuthDataAccelerate(auth ?? null)
                    )
                  }
                />
              )}
              {seeHourly && (
                <ItemMenu
                  testID="hourly"
                  icon="Clock"
                  title="Hora em Hora"
                  path="/hora-em-hora"
                  onNavigate={registerNavigation}
                />
              )}
            </VStack>
            <Box h="1px" bg="gray.600" opacity={0.6} my={10} w="full" maxW="193px" mr={6} />
            <VStack space={4}>
              <ItemMenu
                testID="global-support"
                icon="Support"
                title="Suporte"
                onPress={handleToggleZendesk}
              />
              <ItemMenu
                testID="global-logout"
                icon="GoOut"
                title="Sair do Turbo"
                onPress={handleLogout}
              />
            </VStack>
          </Box>
          <Box position="relative" left={4} mt={10}>
            <Text
              fontSize="14px"
              lineHeight="20px"
              color="white"
              fontWeight={600}>{`versão ${Appconfig.version}`}</Text>
          </Box>
        </Box>
      </ScrollView>

      <Box
        position="absolute"
        right={0}
        bottom={0}
        zIndex={1}
        borderBottomRightRadius="20px"
        overflow="hidden">
        <Illustrations.FlameSantanderTurbo />
      </Box>
    </VStack>
  )
}

const styles = StyleSheet.create({
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    backgroundImage: 'linear-gradient(146.76deg, #494848 4.39%, #393939 42.03%, #070707 94.18%)'
  }
})
